

import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Stack, useMediaQuery } from '@mui/system';
import { Card, Link, Checkbox, MenuItem, useTheme, IconButton, Typography, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { Contact, ContactSource } from 'src/services/contacts/contacts.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import ContactsNotesModal from 'src/components/custom-tables/contacts-table/contacts-notes-modal';

type Props = {
  row: Contact,
  selected?: boolean;
  onSelectRow?: VoidFunction;
  onRemoveItem?: VoidFunction;
};

export function ListItemContactTableRow({
  row,
  selected,
  onSelectRow,
  onRemoveItem
}: Props) {
  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showNotesModal = useBoolean();

  const popover = usePopover();

  const router = useRouter();

  const confirm = useBoolean();

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }}>
        <CardContent>
          <Stack direction="column" alignItems="start" mb={1}>
            <Typography variant="h6">
              {`${row.first_name} ${row.last_name ?? ''}`}
            </Typography>
            {
              (row.account && row.account.organization) && (
                <Typography variant='caption' sx={{ color: theme.palette.text.primary }}>
                  {row.account.organization.company_name}
                </Typography>
              )
            }
          </Stack>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {row.email ?? '-'}
          </Typography>

          {
            (row.phone) && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {row.phone ?? '-'}
              </Typography>
            )
          }

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
            <Tooltip
              title={
                row?.source === ContactSource.JOB_INTERACTION
                  ? t('contacts.source.tooltip_job_interaction')
                  : t('contacts.source.tooltip_manual')
              }
            >
              <Label
                variant="soft"
                color={row?.source === ContactSource.JOB_INTERACTION ? 'success' : 'info'}
              >
                {t(`enums.contact_source.${row?.source}`)} ({row.contact_type ? t(`enums.contact_type.${row?.contact_type}`) : '-'})
              </Label>
            </Tooltip>

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                sx={{ position: 'absolute', top: 8, right: 8, zIndex: 50 }}
                color={popover.open ? 'primary' : 'default'}
                onClick={popover.onOpen}
              >
                <Iconify icon="eva:more-horizontal-fill" />
              </IconButton>
            </Stack>

          </Stack>
        </CardContent>

        <CustomPopover
          open={popover.open}
          onClose={popover.onClose}
          arrow="right-top"
        >
          <MenuItem
            onClick={() => {
              popover.onClose();
              router.push(paths.dashboard.contacts.detail(row.id));
            }}
          >
            <Iconify icon="solar:eye-bold" />
            {t('common.view')}
          </MenuItem>

          <MenuItem
            onClick={() => {
              popover.onClose();
              showNotesModal.onTrue();
            }}
          >
            <Iconify icon="mdi:comments" />
            {t('common.view_notes')}
          </MenuItem>

          <MenuItem
            onClick={onRemoveItem}
          >
            <Iconify icon="ix:remove-circle-filled" color="red" />
            {t('lists.actions.remove_from_list')}
          </MenuItem>


        </CustomPopover>

        <ContactsNotesModal
          threadId={row.thread.id}
          open={showNotesModal.value}
          onClose={showNotesModal.onFalse}
        />

      </Card>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox checked={selected} onClick={onSelectRow} />
        </TableCell>
        <TableCell>
          <Stack direction="column" alignItems="start">
            <Link
              component={RouterLink}
              noWrap
              color="inherit"
              href={paths.dashboard.contacts.detail(row.id)}
            >
              <TruncatedTextField
                text={`${row.first_name} ${row.last_name ?? ''}`}
                mode="characters"
                limit={50}
                showMoreEnabled={false}
                variant="subtitle2"
                fontWeight="bold"
                style={{ textTransform: 'capitalize' }}
              />
            </Link>
            {
              (row.account && row.account.organization) && (
                <Typography variant='caption' sx={{ color: theme.palette.text.primary }}>
                  {row.account.organization.company_name}
                </Typography>
              )
            }

          </Stack>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.email ?? '-'}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.phone ?? '-'}
          </Typography>
        </TableCell>

        <TableCell>
          <Tooltip
            title={
              row?.source === ContactSource.JOB_INTERACTION
                ? t('contacts.source.tooltip_job_interaction')
                : t('contacts.source.tooltip_manual')
            }
          >
            <Label
              variant="soft"
              color={row?.source === ContactSource.JOB_INTERACTION ? 'success' : 'info'}
            >
              {t(`enums.contact_source.${row?.source}`)}
            </Label>
          </Tooltip>
        </TableCell>

        <TableCell sx={{ textTransform: 'capitalize' }}>{row.contact_type ? t(`enums.contact_type.${row?.contact_type}`) : '-'}</TableCell>

        <TableCell align="right" sx={{ px: 4 }}>
          <Tooltip title={t('common.actions')}>
            <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
              <Iconify icon="eva:more-horizontal-fill" />
            </IconButton>
          </Tooltip>
        </TableCell>

      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
      >
        <MenuItem
          onClick={() => {
            popover.onClose();
            router.push(paths.dashboard.contacts.detail(row.id));
          }}
        >
          <Iconify icon="solar:eye-bold" />
          {t('common.view')}
        </MenuItem>

        <MenuItem
          onClick={() => {
            popover.onClose();
            showNotesModal.onTrue();
          }}
        >
          <Iconify icon="mdi:comments" />
          {t('common.view_notes')}
        </MenuItem>

        <MenuItem
          onClick={onRemoveItem}
        >
          <Iconify icon="ix:remove-circle-filled" color="red" />
          {t('lists.actions.remove_from_list')}
        </MenuItem>

      </CustomPopover>

      <ContactsNotesModal
        threadId={row.thread.id}
        open={showNotesModal.value}
        onClose={showNotesModal.onFalse}
      />
    </>
  );
}
